'use client';

import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Card } from '@ironhack/design-system2/components/card';
import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import { Stepper as StepperDs } from '@ironhack/design-system2/components/stepper';
import { type As, type ComponentWithAs } from '@ironhack/design-system2/types';
import { type StructuredTextGraphQlResponse } from 'react-datocms/structured-text';

import { InfoMedia } from '@/app/components/server/info-media';
import { ModuleWrapper } from '@/app/components/server/module-wrapper';
import { type DatoModuleFormat } from '@/lib/datocms';
import { ReskillingList } from './reskilling-list';

type Props = {
  listTitle: string;
  infoMedia: Omit<InfoMedia, 'moduleFormat'>;
  list: StructuredTextGraphQlResponse;
  stepper: {
    moduleFormat: DatoModuleFormat[];
    steps: Array<{
      description: string;
      title: string;
    }>;
    title: string;
    variant: 'electricBlue' | 'oceanBlue' | 'pink' | 'purple' | 'turquoise';
  };
  title: string;
} & BoxProps;

export const Reskilling: ComponentWithAs<As, Props> = forwardRef(
  (props: Props, ref) => {
    const {
      infoMedia: { button: [button] = [], ...infoMedia },
      stepper,
      list,
      listTitle,
      ...boxProps
    } = props;

    const infoMediaData: InfoMedia = {
      ...infoMedia,
      ...(button && {
        button: [{ rel: 'nofollow', tracking: { f: 'reskilling' }, ...button }],
      }),
      headingProps: {
        as: 'h3' as const,
        backgroundColor: '#249EC80D',
        borderRadius: '8',
        pb: '0.5',
        pt: '0.25',
        px: '2',
        textColor: '#249EC8',
        textStyle: ['5xl', null, '3xl'],
        w: 'fit-content',
      },
      mediaSide: 'right' as const,
      moduleFormat: [
        {
          background: 'white',
          spacingBottom: '48',
          spacingTop: '0',
        },
      ],
    };

    return (
      <ModuleWrapper
        moduleFormat={{
          background: 'light',
          spacingBottom: '40',
          spacingTop: '0',
        }}
        outerProps={boxProps}
        ref={ref}
      >
        <Card as="section" p="4" w="full">
          <InfoMedia data={infoMediaData} innerBoxProps={{ px: 0 }} w="full" />
          <StepperDs
            background={stepper.moduleFormat[0].background}
            steps={stepper.steps}
            title={stepper.title}
            variant={stepper.variant}
          />
          <ReskillingList list={list} listTitle={listTitle} />
        </Card>
      </ModuleWrapper>
    );
  }
);
